import { DatePipe } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
} from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { faAsterisk } from "@fortawesome/free-solid-svg-icons";
import { Placement } from "@ng-bootstrap/ng-bootstrap/util/positioning";
import { IonicPage, LoadingController, ViewController } from "ionic-angular";
import * as _ from "lodash";
import moment from "moment";
import { Subscription } from "rxjs";
import { AuthService } from "../../../app/auth/auth.service";
import PatientChart from "../../../models/patient-chart/patientChartResponse";
import { CredentialedResourcePipe } from "../../../pipes/output-formatters/credentialed-resource.pipe";
import { CareTeamsService } from "../../../providers/care-teams-service/care-teams-service";
import { NavBarServiceProvider } from "../../../providers/nav-bar-service/nav-bar-service";
import { ReferralsServiceProvider } from "../../../providers/patient-charts/referrals-service";
import { ResourceManagementService } from "../../../providers/resource-management/resource-management.service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { BenefitPeriod } from "../../models/cti";
import { Hospice } from "../../models/hospice";
import { CertificationService } from "../../services/certification-service";

@IonicPage()
@Component({
  selector: "verbal-certification-form",
  templateUrl: "verbal-certification-form.html",
})
export class VerbalCertificationForm {
  hasValidCertifyingPhysicianName = true;
  hasValidAttendingPhysicianName = true;

  /**
   * CO-3354 — the user-editable controls, used to decide whether an edit
   * actually changed anything (see hasUnsavedFieldChanges).
   *
   * Deliberately an INCLUDE list rather than "everything except
   * reasonForChange": certifyingReceivedByQbosId / attendingReceivedByQbosId are
   * bookkeeping that retrieveUser() fills in ASYNCHRONOUSLY after the edit data
   * is loaded, so including them would flip the comparison to "changed" with no
   * user input and defeat the guard entirely.
   */
  private static readonly EDITABLE_CONTROLS = [
    "benefitPeriod",
    "certifyingPhysician",
    "certifyingObtainedOn",
    "certifyingReceivedBy",
    "attendingPhysician",
    "attendingObtainedOn",
    "attendingReceivedBy",
  ];

  /** Snapshot of the editable values as loaded; null while creating. */
  private editBaseline: string = null;
  allPhysicianDisplayNames: string[] = [];
  protected hasValidAttendingUsername = false;
  protected hasValidCertifyingUsername = false;
  private physicianData = {};
  private hospice: Hospice;
  private patientChart: PatientChart;

  public showAttendingPhysicianOptions = false;
  public attendingPhysicianOptions: any[] = [];

  public showCertifyingPhysicianOptions = false;
  public certifyingPhysicianOptions: any[] = [];

  public showBenefitPeriods = false;
  public benefitPeriods: BenefitPeriod[];
  public benefitPeriod: BenefitPeriod;

  private patientSubscription: Subscription;

  public certTypeControl: FormControl;

  public verbalDisabled = false;
  public writtenDisabled = false;

  public physicians: any[] = [];
  public disabledBtn = false;

  spinner: any = null;
  spinnerCounter: number = 0;
  selectedPhysician: any;
  patientId: string;
  patientData: any;
  attestationPatient;
  obtainedFromUser: string;
  currentUserFullName: string;
  currentUserQbosId: number;
  usersData: any;
  obtainedFromUserQbosId: number;
  duplicateBenefit: boolean = false;
  id: number = 0;

  certificationForm: FormGroup;

  currentDate = new Date();
  currentDateFormat = {
    year: this.currentDate.getFullYear(),
    month: this.currentDate.getMonth() + 1,
    day: this.currentDate.getDate(),
  };

  minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 100));
  minDateFormat = {
    year: this.minDate.getFullYear(),
    month: this.minDate.getMonth() + 1,
    day: this.minDate.getDate(),
  };

  maxDate = new Date(new Date().setFullYear(new Date().getFullYear() + 100));
  maxDateFormat = {
    year: this.maxDate.getFullYear(),
    month: this.maxDate.getMonth() + 1,
    day: this.maxDate.getDate(),
  };

  startMinDate: any = this.minDateFormat;
  startMaxDate: any = this.currentDateFormat;

  endMinDate: any = this.currentDateFormat;
  endMaxDate: any = this.maxDateFormat;

  datePlacementPreference: Placement = "left-top";
  requiredIcon = faAsterisk;

  @Input() verbalCertifications: any;
  @Input() verbalCertificationBenefitsIds: number[] = [];
  @Input() isOnService: false;
  @Input() origin: string;
  @Input() type: string;
  @Input() editData: any = {};
  @Output() isValidChanged = new EventEmitter<boolean>();
  @Output() isSubmitted = new EventEmitter();

  constructor(
    public referralsService: ReferralsServiceProvider,
    private viewCtrl: ViewController,
    private stateData: StateManagementService,
    private renderer: Renderer2,
    private elRef: ElementRef,
    public loadingCtrl: LoadingController,
    private certificationSvc: CertificationService,
    private navBarService: NavBarServiceProvider,
    private datePipe: DatePipe,
    private careTeamService: CareTeamsService,
    public credentialedResourcePipe: CredentialedResourcePipe,
    private authSvc: AuthService,
    private resourceManagementService: ResourceManagementService
  ) {
    this.certTypeControl = new FormControl("");

    this.certificationForm = new FormGroup(
      {
        benefitPeriod: new FormControl(""),
        certifyingPhysician: new FormControl(""),
        certifyingObtainedOn: new FormControl(""),
        certifyingReceivedBy: new FormControl(
          "",
          Validators.compose([Validators.maxLength(150)])
        ),
        attendingPhysician: new FormControl(""),
        attendingObtainedOn: new FormControl(""),
        attendingReceivedBy: new FormControl(
          "",
          Validators.compose([Validators.maxLength(150)])
        ),
        reasonForChange: new FormControl(""),
        certifyingReceivedByQbosId: new FormControl(""),
        attendingReceivedByQbosId: new FormControl(""),
      },
      {
        validators: [this.validatorRequiredPhysicians],
      }
    );

    this.certificationForm
      .get("certifyingObtainedOn")
      .disable({ emitEvent: false });
    this.certificationForm
      .get("attendingObtainedOn")
      .disable({ emitEvent: false });

    this.certificationForm.statusChanges.subscribe((s) =>
      this.isValidChanged.emit(s == "VALID")
    );

    this.hospice = null;

    this.certificationSvc.getHospiceInfo().subscribe((data) => {
      this.hospice = data;
    });

    this.patientId = this.stateData.getChartId();

    this.patientData = this.stateData.selectedPatientData;
    this.patientChart = this.navBarService.getPatientChart();

    if (this.referralsService.PATIENT_STATUS === "referral") {
      const patientChartInfo = JSON.parse(
        window.sessionStorage.getItem("patientChartInfo")
      );
      if (
        patientChartInfo &&
        patientChartInfo.fullName &&
        patientChartInfo.id
      ) {
        this.attestationPatient = {
          name: patientChartInfo.fullName,
          id: patientChartInfo.id,
        };
      } else if (this.patientChart) {
        this.attestationPatient = {
          name: this.patientChart.patient.fullName,
          id: this.patientChart.externalId,
        };
      }
    } else {
      if (this.patientData) {
        this.attestationPatient = {
          name: this.patientData.firstName + " " + this.patientData.lastName,
          id: this.patientData.externalId,
        };
      } else if (this.patientChart) {
        this.attestationPatient = {
          name: this.patientChart.patient.fullName,
          id: this.patientChart.externalId,
        };
      } else {
        this.patientId = window.location.href.split("/")[5];
        this.patientSubscription = careTeamService
          .getPatientInfo(this.navBarService.getExternalId().toString())
          .subscribe((x) => {
            this.patientData = x;
            this.attestationPatient = {
              name: this.patientData.displayName,
              id:
                this.patientData.patientChart === undefined
                  ? 0
                  : this.patientData.patientChart.qbosId,
            };
          });
      }
    }

    this.certificationForm
      .get("attendingReceivedBy")
      .valueChanges.subscribe((val) => {
        this.hasValidAttendingUsername =
          val === this.currentUserFullName ||
          val === this.editData.attendingReceiverName;
      });

    this.certificationForm
      .get("certifyingReceivedBy")
      .valueChanges.subscribe((val) => {
        this.hasValidCertifyingUsername =
          val === this.currentUserFullName ||
          val == this.editData.certifyingReceiverName;
      });

    this.certificationForm
      .get("attendingPhysician")
      .valueChanges.subscribe((val) => {
        this.searchPhysician(val, "attendingPhysician");

        if (
          val &&
          this.allPhysicianDisplayNames &&
          this.allPhysicianDisplayNames.includes(val)
        ) {
          const matchedPhysician = this.findPhysicianByExactDisplayName(val);
          if (matchedPhysician) {
            this.physicianData["attendingPhysician"] = matchedPhysician;
            this.physicianData[
              "attendingPhysician"
            ].displayName = `${matchedPhysician.firstName} ${matchedPhysician.lastName}`;
            this.setPhysicianOptions([], false, "attendingPhysician");
          }
        } else if (val && val.length === 0) {
          delete this.physicianData["attendingPhysician"];
        }

        if (val && val.length > 0) {
          this.certificationForm
            .get("attendingObtainedOn")
            .enable({ emitEvent: false });
        } else {
          this.certificationForm.get("attendingObtainedOn").setValue("");
          this.certificationForm
            .get("attendingObtainedOn")
            .disable({ emitEvent: false });
        }
      });

    this.certificationForm
      .get("certifyingPhysician")
      .valueChanges.subscribe((val) => {
        this.searchPhysician(val, "certifyingPhysician");

        if (
          val &&
          this.allPhysicianDisplayNames &&
          this.allPhysicianDisplayNames.includes(val)
        ) {
          const matchedPhysician = this.findPhysicianByExactDisplayName(val);
          if (matchedPhysician) {
            this.obtainedFromUserQbosId = matchedPhysician.externalId;
            this.physicianData["certifyingPhysician"] = matchedPhysician;
            this.physicianData[
              "certifyingPhysician"
            ].displayName = `${matchedPhysician.firstName} ${matchedPhysician.lastName}`;
            this.setPhysicianOptions([], false, "certifyingPhysician");
          }
        } else if (val && val.length === 0) {
          delete this.physicianData["certifyingPhysician"];
        }

        if (val && val.length > 0) {
          this.certificationForm
            .get("certifyingObtainedOn")
            .enable({ emitEvent: false });
        } else {
          this.certificationForm.get("certifyingObtainedOn").setValue("");
          this.certificationForm
            .get("certifyingObtainedOn")
            .disable({ emitEvent: false });
        }
      });

    this.certificationForm
      .get("certifyingPhysician")
      .valueChanges.subscribe((val) => {
        this.hasValidCertifyingPhysicianName =
          this.allPhysicianDisplayNames.includes(val);
      });
    this.certificationForm
      .get("attendingPhysician")
      .valueChanges.subscribe((val) => {
        if (val && val.length > 0) {
          this.hasValidAttendingPhysicianName =
            this.allPhysicianDisplayNames.includes(val);
        } else {
          this.hasValidAttendingPhysicianName = true;
        }
      });
  }

  ngOnInit() {
    this.renderer.addClass(
      this.elRef.nativeElement.parentNode,
      "modal-cti-confirmation"
    );
    this.retrieveUser();

    this.certificationSvc
      .getPhysicianDataCredential()
      .subscribe((response: string[]) => {
        this.physicians = response;
        this.allPhysicianDisplayNames = this.physicians.map((physician) => {
          return physician.credentials
            ? `${physician.lastName}, ${physician.firstName} (${physician.credentials})`
            : `${physician.lastName}, ${physician.firstName}`;
        });
      });

    if (Object.keys(this.editData).length > 0) {
      this.certificationSvc
        .getPreviousCurrentAndSoonBenefits(this.patientId)
        .subscribe((benefitPeriods: BenefitPeriod[]) => {
          this.benefitPeriods = benefitPeriods.filter(
            (b) =>
              b.benefitPeriod != 0 &&
              (!this.verbalCertificationBenefitsIds.includes(b.id) ||
                b.id == this.editData.benefitPeriodId)
          );
        });
      this.setEditData(this.editData);
    }

    if (this.id == 0) {
      this.certificationSvc
        .getPreviousCurrentAndSoonBenefits(this.patientId)
        .subscribe(
          (benefitPeriod: BenefitPeriod[]) => {
            this.initializeBenefitPeriods(
              benefitPeriod.filter(
                (b) =>
                  b.benefitPeriod != 0 &&
                  !this.verbalCertificationBenefitsIds.includes(b.id)
              )
            );
          },
          (error) => {
            console.error(error);
          }
        );
    }
  }

  initializeBenefitPeriods(benefitPeriods: BenefitPeriod[]) {
    this.benefitPeriods = benefitPeriods.filter((b) => b.benefitPeriod != 0);

    const currentBenefitPeriod = benefitPeriods.find(
      (benefit) => benefit.isCurrentBenefitPeriod
    );

    if (currentBenefitPeriod) {
      this.benefitPeriod = currentBenefitPeriod;
    }
  }

  formatCertificationValue(benefit: BenefitPeriod) {
    return `${this.datePipe.transform(
      benefit.startDate,
      "MM/dd/yyyy"
    )} To ${this.datePipe.transform(benefit.endDate, "MM/dd/yyyy")}`;
  }

  isValidPhysicianData = (
    physicianNameControl: AbstractControl,
    obtainedOnControl: AbstractControl,
    receivedByControl: AbstractControl,
    editDataName: String
  ): boolean => {
    return (
      physicianNameControl.value != null &&
      physicianNameControl.value.length != 0 &&
      obtainedOnControl.value != null &&
      obtainedOnControl.value.length != 0 &&
      receivedByControl.value != null &&
      receivedByControl.value.length != 0 &&
      (receivedByControl.value == this.currentUserFullName ||
        receivedByControl.value == editDataName)
    );
  };

  isEmptyPhysicianData = (
    physicianNameControl: AbstractControl,
    obtainedOnControl: AbstractControl,
    receivedByControl: AbstractControl
  ): boolean => {
    return (
      (physicianNameControl.value == null ||
        physicianNameControl.value.length == 0) &&
      (obtainedOnControl.value == null ||
        obtainedOnControl.value.length == 0) &&
      (receivedByControl.value == null || receivedByControl.value.length == 0)
    );
  };
  validatorRequiredPhysicians = (group: FormGroup) => {
    let filledCertifyingPhysician = this.isValidPhysicianData(
      group.controls["certifyingPhysician"],
      group.controls["certifyingObtainedOn"],
      group.controls["certifyingReceivedBy"],
      this.editData.certifyingReceiverName
    );
    let filledAttendingPhysician = this.isValidPhysicianData(
      group.controls["attendingPhysician"],
      group.controls["attendingObtainedOn"],
      group.controls["attendingReceivedBy"],
      this.editData.attendingReceiverName
    );

    let emptyAttendingPhysician = this.isEmptyPhysicianData(
      group.controls["attendingPhysician"],
      group.controls["attendingObtainedOn"],
      group.controls["attendingReceivedBy"]
    );

    if (this.id > 0) {
      if (!this.reasonForChange) {
        return { notValidReasonOfChangeData: true };
      }
      // CO-3354 — block an edit that changes nothing.
      //
      // "Reason for change" is MANDATORY on every edit, but the audit trail is
      // written per CHANGED field (patient-certification's
      // createAndSaveCertificationHistoryForPatientChart appends a row only
      // where an old value differs from the new one). So a save that altered
      // nothing used to be accepted, fire a PUT, close the modal as if it had
      // saved — and silently discard the justification the user was forced to
      // type, leaving no record the edit ever happened. On a Medicare
      // compliance document that is the worst combination: the user believes
      // they recorded something and the audit trail disagrees.
      if (!this.hasUnsavedFieldChanges(group)) {
        return { noFieldChangesToSave: true };
      }
    }

    if (
      filledCertifyingPhysician &&
      this.hasValidCertifyingPhysicianName &&
      (emptyAttendingPhysician ||
        (filledAttendingPhysician && this.hasValidAttendingPhysicianName))
    ) {
      return {};
    } else {
      return { notValidCertifyingData: true };
    }
  };

  protected retrieveUser() {
    this.resourceManagementService
      .getNameForSignatureByExternalId(this.authSvc.getUser().qbosUserId)
      .subscribe((name) => {
        this.currentUserFullName = name.toString();
      });
    this.currentUserQbosId = this.authSvc.getUser().userId;
  }

  findPhysicianByExactDisplayName(displayName: string) {
    if (!displayName || !this.physicians) {
      return null;
    }

    const matchedPhysician = this.physicians.find((physician) => {
      const physicianDisplayName = physician.credentials
        ? `${physician.lastName}, ${physician.firstName} (${physician.credentials})`
        : `${physician.lastName}, ${physician.firstName}`;
      return physicianDisplayName === displayName;
    });

    if (matchedPhysician) {
      return {
        firstName: matchedPhysician.firstName,
        lastName: matchedPhysician.lastName,
        displayName: matchedPhysician.credentials
          ? `${matchedPhysician.lastName}, ${matchedPhysician.firstName} (${matchedPhysician.credentials})`
          : `${matchedPhysician.lastName}, ${matchedPhysician.firstName}`,
        credentials: matchedPhysician.credentials,
        id: matchedPhysician.id,
        qbosId: matchedPhysician.externalId,
        externalId: matchedPhysician.externalId,
      };
    }

    return null;
  }

  searchPhysician(searchTerm: string, fieldName: string) {
    let options = [];
    let showFlag = false;
    if (searchTerm && searchTerm.length > 2 && this.physicians) {
      showFlag = true;
      options = this.physicians
        .map((physician) => {
          return {
            name:
              (physician.firstName + " " + physician.lastName).toLowerCase() +
              "(" +
              physician.credentials +
              ")",
            firstName: physician.firstName,
            lastName: physician.lastName,
            displayName: physician.credentials
              ? `${physician.lastName}, ${physician.firstName} (${physician.credentials})`
              : `${physician.lastName}, ${physician.firstName}`,
            credentials: physician.credentials,
            id: physician.id,
            qbosId: physician.externalId,
          };
        })
        .filter((data) => {
          return (
            data.name.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1
          );
        });
      this.setPhysicianOptions(
        _.sortBy(options, "lastName"),
        showFlag,
        fieldName
      );
    } else {
      this.setPhysicianOptions(options, showFlag, fieldName);
    }
  }

  setBenefitPeriod(benefitPeriod: BenefitPeriod) {
    this.benefitPeriod = benefitPeriod;
    this.showBenefitPeriods = false;
    this.certificationForm
      .get("benefitPeriod")
      .setValue(this.formatCertificationValue(benefitPeriod));
  }

  setPhysicianOptions(options: string[], showFlag: boolean, fieldName: string) {
    switch (fieldName) {
      case "attendingPhysician":
        this.attendingPhysicianOptions = options;
        this.showAttendingPhysicianOptions = showFlag;
        break;
      case "certifyingPhysician":
        this.certifyingPhysicianOptions = options;
        this.showCertifyingPhysicianOptions = showFlag;
        break;
      default:
    }
  }

  setPhysician(phys, control: string) {
    const physControl = this.certificationForm.get(control);
    if (physControl) {
      physControl.setValue(phys.displayName);
      this.obtainedFromUserQbosId = phys.externalId;
      this.physicianData[control] = phys;
      this.physicianData[
        control
      ].displayName = `${phys.firstName} ${phys.lastName}`;
      this.setPhysicianOptions([], false, control);
    }
  }

  /**
   * CO-3354 — stable snapshot of the user-editable values, for change
   * detection. Uses getRawValue() because the obtained-on controls are
   * disabled, and normalises null/undefined to "" so a value that merely
   * round-trips through the form does not read as a change.
   */
  private substantiveSnapshot(group: FormGroup): string {
    const raw = group.getRawValue();
    return JSON.stringify(
      VerbalCertificationForm.EDITABLE_CONTROLS.map((name) => {
        const value = raw[name];
        return value === null || value === undefined ? "" : String(value);
      })
    );
  }

  /**
   * CO-3354 — true when the user has actually altered a field since the edit
   * was opened. Always true while creating (no baseline), so the guard only
   * ever applies to edits.
   */
  hasUnsavedFieldChanges(group: FormGroup): boolean {
    if (this.editBaseline === null) {
      return true;
    }
    return this.substantiveSnapshot(group) !== this.editBaseline;
  }

  setEditData(data) {
    const user = this.authSvc.getUser();
    this.id = data.id;
    this.certificationForm
      .get("certifyingPhysician")
      .setValue(data.certifyingPhysician);
    if (data.certifyingPhysicianRM) {
      this.certificationForm
        .get("certifyingPhysician")
        .setValue(
          this.credentialedResourcePipe.transform(data.certifyingPhysicianRM)
        );
    }
    this.certificationForm
      .get("certifyingObtainedOn")
      .setValue(data.certifyingReceivedDate);
    this.certificationForm
      .get("certifyingReceivedBy")
      .setValue(data.certifyingReceiverName);
    if (data.attendingPhysician) {
      this.certificationForm
        .get("attendingPhysician")
        .setValue(data.attendingPhysician);
      if (data.attendingPhysicianRM) {
        this.certificationForm
          .get("attendingPhysician")
          .setValue(
            this.credentialedResourcePipe.transform(data.attendingPhysicianRM)
          );
      }
      this.certificationForm
        .get("attendingObtainedOn")
        .setValue(data.attendingReceivedDate);
      this.certificationForm
        .get("attendingReceivedBy")
        .setValue(data.attendingReceiverName);
    }

    this.origin = data.origin;
    const benefitPer = {} as BenefitPeriod;
    benefitPer.id = data.benefitPeriodId;
    benefitPer.benefitPeriod = data.benefitPeriod;
    benefitPer.startDate = data.fromDate;
    benefitPer.endDate = data.toDate;
    this.benefitPeriod = benefitPer;

    this.physicianData["certifyingPhysician"] = {};
    this.physicianData["attendingPhysician"] = {};

    if (this.physicianData["certifyingPhysician"]) {
      this.physicianData["certifyingPhysician"].displayName =
        data.certifyingPhysician;
      this.physicianData["certifyingPhysician"].qbosId =
        data.certifyingObtainedFromQbosId;
      this.physicianData["certifyingPhysician"].id =
        data.certifyingObtainedFromId;
    }

    if (this.physicianData["attendingPhysician"]) {
      this.physicianData["attendingPhysician"].displayName =
        data.attendingPhysician;
      this.physicianData["attendingPhysician"].qbosId =
        data.attendingObtainedFromQbosId;
      this.physicianData["attendingPhysician"].id =
        data.attendingObtainedFromId;
    }

    this.hasValidCertifyingPhysicianName = true;
    this.hasValidAttendingPhysicianName = true;

    this.certificationForm
      .get("certifyingReceivedByQbosId")
      .setValue(user.userId);
    this.certificationForm
      .get("attendingReceivedByQbosId")
      .setValue(user.userId);

    this.type = data.type;
    this.setBenefitPeriod(this.benefitPeriod);

    // CO-3354 — capture the loaded values LAST (setBenefitPeriod above writes
    // the benefitPeriod control), so hasUnsavedFieldChanges compares against
    // what the user was actually shown.
    this.editBaseline = this.substantiveSnapshot(this.certificationForm);
    this.certificationForm.updateValueAndValidity({ emitEvent: true });
  }

  async onSubmit() {
    const user = this.authSvc.getUser();

    let certifyingReceivedByQbosId;
    if (this.certifyingReceivedBy == this.editData.certifyingReceiverName) {
      certifyingReceivedByQbosId = this.editData.certifyingReceivedByQbosId;
    } else {
      certifyingReceivedByQbosId = user.userId;
    }
    let attendingReceivedByQbosId;
    if (this.attendingReceivedBy == this.editData.attendingReceiverName) {
      attendingReceivedByQbosId = this.editData.attendingReceivedByQbosId;
    } else if (
      this.attendingReceivedBy !== "" &&
      this.attendingPhysician !== ""
    ) {
      attendingReceivedByQbosId = user.userId;
    } else {
      attendingReceivedByQbosId = null;
    }
    let patientAdmitDate =
      this.patientChart && this.patientChart.admittedDatetime
        ? moment(this.patientChart.admittedDatetime).format("YYYY-MM-DD")
        : null;
    const payload = {
      id: this.id,
      origin: this.origin,
      type: this.type,
      patientQbosId: parseInt(this.patientId),
      certifyingObtainedFromQbosId: this.certifyingObtainedFromQbosId,
      certifyingObtainedFromId: this.certifyingObtainedFromId,
      attendingObtainedFromQbosId:
        this.attendingPhysician !== ""
          ? this.attendingObtainedFromQbosId
          : null,
      attendingObtainedFromId:
        this.attendingPhysician !== "" ? this.attendingObtainedFromId : null,
      certifyingPhysicianSignedDate: this.certifyingObtainedOn,
      attendingPhysicianSignedDate: this.attendingObtainedOn,
      certifyingPhysicianName: this.certifyingPhysicianToSave,
      attendingPhysicianName:
        this.attendingPhysician !== ""
          ? this.attendingPhysicianToSave
          : this.attendingPhysician,
      certifyingReceivedBy: this.certifyingReceivedBy,
      attendingReceivedBy: this.attendingReceivedBy,
      benefitPeriod: this.benefitPeriod ? this.benefitPeriod.id : null,
      reasonForChange: this.reasonForChange,
      certifyingReceivedByQbosId,
      attendingReceivedByQbosId,
      patientAdmitDate,
    };

    if (this.id == 0) {
      delete payload.id;
      delete payload.reasonForChange;
    }

    payload["hospice"] = this.hospice;

    let response: any;
    try {
      if (this.id > 0) {
        if (this.type == "VERBAL_REFERRAL_V2") {
          response = await this.certificationSvc
            .Referral_UpdateVerbalCtiv2(payload)
            .toPromise();
        } else if (this.type == "VERBAL_PATIENT_CHART_V2") {
          response = await this.certificationSvc
            .PatientChart_UpdateVerbalCtiv2(payload)
            .toPromise();
        } else {
          return {};
        }
      } else {
        if (this.type == "VERBAL_REFERRAL_V2") {
          response = await this.certificationSvc
            .Referral_CreateVerbalCTIv2(payload)
            .toPromise();
        } else if (this.type == "VERBAL_PATIENT_CHART_V2") {
          response = await this.certificationSvc
            .PatientChart_CreateVerbalCTIv2(payload)
            .toPromise();
        } else {
          return {};
        }
      }

      if (response) {
        return {};
      }
    } catch (err) {
      if (err) {
        return { error: err.message };
      }
    }
    return {};
  }

  onDismiss() {
    this.viewCtrl.dismiss({ cancel: true });
    this.disabledBtn = false;
  }

  // incremental loader function
  increaseSpinnerCount(message: string = "Creating Assessment...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  // decremental loader function
  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  ionViewDidLeave() {
    if (this.patientSubscription) {
      this.patientSubscription.unsubscribe();
    }
  }

  get patientQbosId() {
    return parseInt(this.patientId);
  }

  get certifyingPhysician() {
    return this.certificationForm.get("certifyingPhysician").value;
  }

  get certifyingPhysicianToSave() {
    if (this.physicianData["certifyingPhysician"]) {
      return this.physicianData["certifyingPhysician"].displayName;
    } else {
      return this.certificationForm.get("certifyingPhysician").value;
    }
  }

  get certifyingObtainedOn() {
    let now = new Date();
    return moment(this.certificationForm.get("certifyingObtainedOn").value)
      .utc()
      .hour(now.getHours())
      .minute(now.getMinutes())
      .seconds(now.getSeconds())
      .toISOString();
  }

  get certifyingReceivedBy() {
    return this.certificationForm.get("certifyingReceivedBy").value;
  }

  get attendingPhysician() {
    return this.certificationForm.get("attendingPhysician").value;
  }

  get attendingPhysicianToSave() {
    if (this.physicianData["attendingPhysician"]) {
      return this.physicianData["attendingPhysician"].displayName;
    } else {
      return this.certificationForm.get("attendingPhysician").value;
    }
  }

  get attendingObtainedOn() {
    let now = new Date();
    return moment(this.certificationForm.get("attendingObtainedOn").value)
      .utc()
      .hour(now.getHours())
      .minute(now.getMinutes())
      .seconds(now.getSeconds())
      .toISOString();
  }

  get attendingReceivedBy() {
    return this.certificationForm.get("attendingReceivedBy").value;
  }

  get certifyingObtainedFromQbosId() {
    if (this.physicianData["certifyingPhysician"]) {
      return parseInt(this.physicianData["certifyingPhysician"].qbosId);
    }
  }

  get certifyingObtainedFromId() {
    if (this.physicianData["certifyingPhysician"]) {
      return parseInt(this.physicianData["certifyingPhysician"].id);
    }
  }

  get attendingObtainedFromQbosId() {
    if (this.physicianData["attendingPhysician"]) {
      return parseInt(this.physicianData["attendingPhysician"].qbosId);
    }
  }

  get attendingObtainedFromId() {
    if (this.physicianData["attendingPhysician"]) {
      return parseInt(this.physicianData["attendingPhysician"].id);
    }
  }

  get reasonForChange() {
    return this.certificationForm.get("reasonForChange").value;
  }
}



// WEBPACK FOOTER //
// ./src/certification/forms/verbal-certification-form/verbal-certification-form.ts